// Microphone-permission helper.
//
// Why this exists: iPadOS Safari's Web Speech API will silently produce no
// result if the mic permission hasn't been granted — there's no `onerror`,
// just a fast `onend` with no transcript. The user blames the game. The fix
// is to explicitly request mic access via getUserMedia *before* the first
// recognition attempt, which guarantees the iOS permission prompt fires.

export type MicState = 'unknown' | 'prompt' | 'granted' | 'denied' | 'unsupported';

export interface MicProbeResult {
  state: MicState;
  error?: string;
}

// Cheap permission read. Returns 'unknown' on Safari (which doesn't support
// permissions.query for microphone) — in that case the caller should fall
// through to ensureMicAccess().
export async function readMicPermission(): Promise<MicState> {
  if (!navigator.mediaDevices?.getUserMedia) return 'unsupported';
  try {
    if (navigator.permissions?.query) {
      const r = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      return r.state as MicState;
    }
  } catch { /* not supported on iOS Safari for 'microphone' */ }
  return 'unknown';
}

// Trigger the iOS permission prompt if needed. Returns the resulting state.
// Caller should show a friendly UI before calling so the user understands
// why the prompt is about to appear.
export async function ensureMicAccess(): Promise<MicProbeResult> {
  if (!navigator.mediaDevices?.getUserMedia) return { state: 'unsupported' };
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // We only wanted to trigger the prompt; release the tracks immediately so
    // we're not holding the mic open until the kid actually speaks.
    stream.getTracks().forEach((t) => t.stop());
    return { state: 'granted' };
  } catch (e) {
    const err = e as Error;
    const denied = err.name === 'NotAllowedError' || err.name === 'SecurityError';
    return { state: denied ? 'denied' : 'unknown', error: `${err.name}: ${err.message}` };
  }
}
